/**
 * React Query hooks for Page CRUD operations.
 * Uses tRPC client for type-safe API calls.
 *
 * Two list hooks are available:
 *  - usePages()         — Returns all pages (legacy, unpaginated)
 *  - usePagesPaginated() — Returns a paginated result with metadata
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import type { Page } from "../types";

// ── Paginated result type ──────────────────────────────────────────────────────
export interface PaginatedPagesResult {
  items: Page[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasMore: boolean;
}

// ── Legacy: list all pages (unpaginated) ───────────────────────────────────────
export function usePages(filters?: { projectId?: string; status?: string }) {
  return useQuery({
    queryKey: ["pages", "list", filters],
    queryFn: () => trpc.page.list.query(filters || {}),
  }) as { data: Page[]; isLoading: boolean; isError: boolean; error: any };
}

// ── Paginated page list ─────────────────────────────────────────────────────────
export function usePagesPaginated(params?: {
  projectId?: string;
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: "pageNumber" | "title" | "status" | "updatedAt" | "createdAt";
  sortOrder?: "asc" | "desc";
}) {
  return useQuery({
    queryKey: ["pages", "paginated", params],
    queryFn: () =>
      trpc.page.listPaginated.query({
        projectId: params?.projectId,
        status: params?.status,
        search: params?.search,
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 20,
        sortBy: params?.sortBy ?? "pageNumber",
        sortOrder: params?.sortOrder ?? "asc",
      }),
  }) as { data: PaginatedPagesResult | undefined; isLoading: boolean; isError: boolean; error: any };
}

// ── Single page ─────────────────────────────────────────────────────────────────
export function usePage(pageId: string) {
  return useQuery({
    queryKey: ["pages", "get", pageId],
    queryFn: () => trpc.page.get.query(pageId),
    enabled: !!pageId,
  }) as { data: Page | undefined; isLoading: boolean; isError: boolean; error: any };
}

// ── Mutations ───────────────────────────────────────────────────────────────────
export function useCreatePage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      key: string;
      title: string;
      description?: string;
      projectId?: string;
      body: string;
      pageNumber?: number;
      status?: string;
      stages?: Record<string, boolean>;
      stageAssignees?: Record<string, string | null>;
    }) => {
      return await trpc.page.create.mutate(input as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pages"] });
      // Backend may have auto-created "pending" characters
      queryClient.invalidateQueries({ queryKey: ["characters"] });
    },
  });
}

export function useUpdatePage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string } & Partial<Page>) => {
      return await trpc.page.update.mutate(input as any);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["pages"] });
      // If layouts were updated, invalidate the specific page
      if ((variables as any).layouts !== undefined) {
        queryClient.invalidateQueries({ queryKey: ["pages", "get", variables.id] });
      }
      // Backend may have auto-created "pending" characters from Fountain text
      queryClient.invalidateQueries({ queryKey: ["characters"] });
    },
  });
}

export function useDeletePage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return await trpc.page.delete.mutate(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pages"] });
    },
  });
}

export function useBatchCreatePages() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { projectId: string; count: number; startPageNumber?: number }) => {
      return await trpc.page.batchCreate.mutate(input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pages"] });
      // Import may create characters
      queryClient.invalidateQueries({ queryKey: ["characters"] });
    },
  });
}

export function useReorderPages() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { projectId: string; pageIds: string[] }) => {
      return await trpc.page.reorder.mutate(input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pages"] });
    },
  });
}